import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import type { BoardroomCatalogueEntry, BoardroomDeliverable } from '../../../types';
import Drawer from '../../common/Drawer';
import BoardList, { BoardButtons, type BoardPanel } from './BoardList';
import GlanceCard from './GlanceCard';
import SlideUpPanel from './SlideUpPanel';
import { dropIndex, reorder, type ListItem } from './listOrder';
import { provenance, titleOfKind } from './provenance';
import { dashboardTiles, viewOf } from './views';

/** A card with nothing in it yet: no rows, no number above zero, and no
 *  text of its own (a note the chat wrote has no rows but is not empty). */
export function isEmptyCard(d: BoardroomDeliverable): boolean {
  return !d.fill.rows && !d.glance?.stats.some(s => s.value) && !d.preview?.length && !d.body;
}

/** How long a table just added to the list glows. */
const GLOW_MS = 5000;

/**
 * The room's board: a dashboard of the tables on its list. An open table has
 * the page's full width; its Tables button opens the list from the left.
 *
 * ONE PLACE FOR A TABLE. It is on the list (and so on the dashboard), or it is
 * in Add. On the dashboard the cards ARE the list; Noticed, Observed and Add
 * sit above the cards. The packed board, its "Not pinned" shelf and Browse were three places
 * to think about; the list is the one, and nothing on it can be hidden, so the
 * dashboard may scroll.
 *
 * Opening a card shows its full table. What the room
 * noticed and observed, and Add, are counts at the bottom of the list; each
 * opens a panel that slides up over the board.
 */
export default function DeliverableBoard({
  deliverables,
  catalogue,
  badges,
  renderCard,
  renderFull,
  expanded,
  onExpand,
  onPin,
  onDelete,
  temporary,
  onSave,
  renderActions,
  onReorder,
  panel,
  onPanel,
  panels,
  matches,
  top,
  onOpenKind,
  onCompare,
  roomKind,
}: {
  /** What kind of question the room asks: a story room groups its tables as a
   *  reporter's views (`views.viewsFor`). */
  roomKind?: string | null;
  /** Open Compare: companies side by side. */
  onCompare?: () => void;
  /** Open one of a view's tables by kind: the room's card when it has one,
   *  otherwise the table unsaved. */
  onOpenKind?: (kind: string) => void;
  /** Above the cards on the dashboard: the room's Brief. */
  top?: ReactNode;
  deliverables: BoardroomDeliverable[];
  catalogue: BoardroomCatalogueEntry[];
  /** Per-kind "landed since you looked" counts. */
  badges?: Record<string, number>;
  /** A card's first few rows, for a card with no numbers of its own. */
  renderCard: (d: BoardroomDeliverable, lines?: number) => ReactNode;
  /** What the table looks like open - the real report. */
  renderFull?: (d: BoardroomDeliverable) => ReactNode;
  /** Which card is open, and how to change it. Held by the page so the report
   *  data for it can be fetched. */
  expanded?: string | null;
  /** `replace` for the board's own moves (off the list, so on to the next):
   *  those are not steps Back should stop at. Your own opens are. */
  onExpand: (d: BoardroomDeliverable | null, replace?: boolean) => void;
  /** On the list or off it. */
  onPin: (d: BoardroomDeliverable, pinned: boolean) => void;
  /** Delete a view for good. Only offered for views that are not built in. */
  onDelete?: (d: BoardroomDeliverable) => void;
  /** A table opened that the room has not kept (its id is below 0): shown
   *  unsaved, with Save and Pin. */
  temporary?: BoardroomDeliverable | null;
  /** Keep the temporary view in All views, without pinning it. */
  onSave?: (d: BoardroomDeliverable) => void;
  /** More buttons for the open view's header, such as Share. */
  renderActions?: (d: BoardroomDeliverable) => ReactNode;
  /** Ask the room to fill one. Goes through the chat so it can be qualified. */
  /** No longer drawn: cards have no "Go get these". Kept so callers compile. */
  onWork?: (d: BoardroomDeliverable) => void;
  /** New positions after a drag, as {id: position} — only what moved. */
  onReorder: (positions: Record<number, number>) => void;
  /** Which panel is up. Held by the page, so the chat and other screens can
   *  open Add. */
  panel: BoardPanel | null;
  onPanel: (p: BoardPanel | null) => void;
  /** What each panel shows (null when there is nothing), and the counts for
   *  Noticed and Observed. */
  panels: {
    noticed: { count: number; body: ReactNode };
    observed: { count: number; body: ReactNode };
    add: ReactNode;
  };
  /** With the room's filter on: per card, how many rows of its whole table
   *  match, counted by the server. A card missing from it shows no count. */
  matches?: Record<number, { hits: number; seen: number }> | null;
}) {
  const matchesOf = (d: BoardroomDeliverable) => matches?.[d.id] ?? null;
  // WHERE EACH CARD CAME FROM, in a line - a filtered copy names its table.
  const titleOf = titleOfKind(deliverables, k => catalogue.find(c => c.kind === k)?.title ?? k);
  const originOf = (d: BoardroomDeliverable) => provenance(d, titleOf);
  const live = useMemo(() => deliverables.filter(d => d.status !== 'dropped'), [deliverables]);
  const listed = useMemo(
    () =>
      live
        .filter(d => d.pinned)
        .slice()
        .sort((a, b) => a.position - b.position || a.id - b.id),
    [live],
  );
  // JUST ADDED. A table that lands on the list glows for a few seconds, in the
  // list and on the dashboard, and scrolls into view - adding one from the Add
  // panel otherwise put it somewhere you had to go looking for. Measured
  // against what was on the list a moment ago, and not on the first load, or
  // opening a room would light up everything.
  const [glowing, setGlowing] = useState<ReadonlySet<number>>(() => new Set());
  const seen = useRef<Set<number> | null>(null);
  const timers = useRef<ReturnType<typeof setTimeout>[]>([]);
  // The latest of these, for the effect below without re-running it on each.
  const latest = useRef({ panel, onPanel, onExpand, listed, reading: !!expanded });
  latest.current = { panel, onPanel, onExpand, listed, reading: !!expanded };
  useEffect(() => {
    // The catalogue is empty only while the board is still loading.
    if (!catalogue.length) return;
    const now = new Set(listed.map(d => d.id));
    const before = seen.current;
    seen.current = now;
    if (!before) return;
    const fresh = Array.from(now).filter(id => !before.has(id));
    if (!fresh.length) return;
    setGlowing(g => new Set([...Array.from(g), ...fresh]));
    // PINNED FROM A PANEL WHILE READING A TABLE, IT OPENS: you were in the
    // list-and-table view, so the new one takes the screen and the panel goes.
    // ON THE DASHBOARD it only glows, and the panel stays for the next one -
    // the dashboard is where you see it. One the chat or the room added
    // while you were doing something else only glows, either way.
    const { panel: from, onPanel: closePanel, onExpand: openIt, listed: now_, reading } = latest.current;
    if (from && reading) {
      const newest = now_.find(d => d.id === fresh[fresh.length - 1]);
      if (newest) {
        closePanel(null);
        openIt(newest);
      }
    }
    timers.current.push(
      setTimeout(() => {
        setGlowing(g => new Set(Array.from(g).filter(id => !fresh.includes(id))));
      }, GLOW_MS),
    );
    // After it is drawn: the newest one into view, wherever it landed. With a
    // panel up over the dashboard, to the TOP - the part the panel leaves
    // uncovered - or it glows where you cannot see it.
    const block: ScrollLogicalPosition = from && !reading ? 'start' : 'nearest';
    requestAnimationFrame(() =>
      document
        .querySelectorAll('[data-just-added="true"]')
        .forEach(el => el.scrollIntoView?.({ block, behavior: 'smooth' })),
    );
  }, [listed, catalogue.length]);
  useEffect(() => () => timers.current.forEach(clearTimeout), []);

  const tiles = useMemo(() => dashboardTiles(listed, roomKind), [listed, roomKind]);
  const tileEmpty = (t: { members: BoardroomDeliverable[] }) => t.members.every(isEmptyCard);
  const open = expanded
    ? live.find(d => `${d.kind}:${d.subject}` === expanded) ??
      (temporary && `${temporary.kind}:${temporary.subject}` === expanded ? temporary : null)
    : null;
  // THE LIST BESIDE AN OPEN TABLE: everything on it, and the open one marked
  // even when it is not on it.
  const columnTiles = useMemo(
    () => dashboardTiles(open && !open.pinned ? [...listed, open] : listed, roomKind),
    [open, listed, roomKind],
  );
  // EVERY VIEW THE ROOM CAN SHOW: the ones it has, pinned or not, and the
  // built-in tables never chosen.
  const addCount = deliverables.length + catalogue.filter(c => !c.chosen).length;
  // A VIEW THAT IS NOT BUILT IN can be deleted: one the chat made, or a page
  // someone kept. A built-in table can only be unpinned - the same rule as in
  // All views.
  const builtIn = new Set(catalogue.map(c => c.kind));
  const deletable = (d: BoardroomDeliverable) => !builtIn.has(d.kind) || !!d.subject;
  const counts = {
    noticed: panels.noticed.count,
    observed: panels.observed.count,
    add: addCount,
  };

  // TAKING ONE OFF THE LIST. Taking off the one you are reading opens the next
  // one down (or the one above, at the bottom), and the dashboard when it was
  // the last: staying on a table you just took off would leave it marked in the
  // list as if the × had not worked.
  const removeFromList = (members: BoardroomDeliverable[]) => {
    members.forEach(m => onPin(m, false));
    if (!open || !members.some(m => m.id === open.id)) return;
    // The next view down (the one above, at the bottom), by the list's order.
    const at = columnTiles.findIndex(t => t.members.some(m => m.id === open.id));
    const rest = columnTiles.filter((_, i) => i !== at);
    const next = rest[at] ?? rest[rest.length - 1] ?? null;
    onExpand(next ? next.lead : null, true);
  };

  // DRAGGING IS HOW A DASHBOARD BECOMES YOURS. `listOrder.reorder` answers
  // "which index did this land on" and returns only the cards that moved.
  const [dragId, setDragId] = useState<number | null>(null);
  const items: ListItem[] = useMemo(
    () => listed.map(d => ({ id: d.id, position: d.position, pinned: true })),
    [listed],
  );
  // WHERE IT WILL LAND: the card under the pointer and which half of it, so a
  // bar can be drawn in the gap on that side and the drop goes exactly there.
  const [over, setOver] = useState<{ id: number; side: 'before' | 'after' } | null>(null);
  const indexOf = (id: number) => listed.findIndex(d => d.id === id);
  const landing = (o: typeof over) =>
    dragId === null || !o ? null : dropIndex(indexOf(dragId), indexOf(o.id), o.side);
  const endDrag = () => {
    setDragId(null);
    setOver(null);
  };
  const dropOn = (target: BoardroomDeliverable, side: 'before' | 'after') => {
    const to = landing({ id: target.id, side });
    const moved = dragId !== null && to !== null ? reorder(items, dragId, to) : {};
    endDrag();
    if (Object.keys(moved).length) onReorder(moved);
  };
  const sideOf = (e: React.DragEvent): 'before' | 'after' => {
    const r = e.currentTarget.getBoundingClientRect();
    return e.clientX < r.left + r.width / 2 ? 'before' : 'after';
  };
  const dragProps = (d: BoardroomDeliverable) => ({
    draggable: true,
    onDragStart: () => setDragId(d.id),
    onDragEnd: endDrag,
    onDragOver: (e: React.DragEvent) => {
      if (dragId === null) return;
      e.preventDefault();
      const side = sideOf(e);
      if (over?.id !== d.id || over.side !== side) setOver({ id: d.id, side });
    },
    onDrop: (e: React.DragEvent) => {
      e.preventDefault();
      dropOn(d, sideOf(e));
    },
  });
  const dropSideOf = (d: BoardroomDeliverable) =>
    over?.id === d.id && landing(over) !== null ? over.side : null;

  // THE LIST HAS NO COLUMN OF ITS OWN: a button in the open table's header
  // opens it from the left. A column beside the table took its width on every
  // screen, for a list you use only to switch tables.
  const [listOpen, setListOpen] = useState(false);
  const list = (className = '') => (
    <BoardList
      tiles={columnTiles}
      isEmpty={isEmptyCard}
      open={open}
      badges={badges}
      matchesOf={matchesOf}
      onOpen={d => {
        setListOpen(false);
        onExpand(d);
      }}
      onRemove={removeFromList}
      counts={counts}
      glowing={glowing}
      panel={panel}
      onPanel={p => {
        setListOpen(false);
        onPanel(p);
      }}
      className={className}
    />
  );

  const PANEL_TITLES: Record<BoardPanel, string> = {
    noticed: 'Noticed',
    observed: 'Observed',
    add: 'Add a view',
  };

  return (
    <div className="flex min-h-0 flex-1 gap-2">
      <div className="relative flex min-h-0 min-w-0 flex-1 flex-col">
        {/* ABOVE THE CARDS, on the dashboard. Beside an open table they are
            at the top of the list its Tables button opens. */}
        {!open && (
          <div className="mb-2 flex flex-wrap items-center gap-2">
            <BoardButtons counts={counts} panel={panel} onPanel={onPanel} row />
            {onCompare && (
              <button
                type="button"
                onClick={onCompare}
                title="Two to four companies side by side: price, pitch, features and complaints"
                className="rounded-md border border-gray-200 bg-white px-2.5 py-1.5 text-xs text-gray-700 hover:border-gray-300 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-200 dark:hover:border-gray-600"
                data-testid="board-compare"
              >
                Compare
              </button>
            )}
          </div>
        )}
        {open ? (
          <section className="flex min-h-0 min-w-0 flex-1 flex-col rounded-lg border border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800">
            <header className="flex items-center gap-2 border-b border-gray-100 px-3 py-1.5 dark:border-gray-700/60">
              <button
                type="button"
                onClick={() => setListOpen(true)}
                title="Switch to another view on the Dashboard"
                className="shrink-0 rounded border border-gray-300 px-1.5 py-0.5 text-xs text-gray-600 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700"
                data-testid="board-list-button"
              >
                ☰ Views
              </button>
              <span className="truncate text-sm font-medium text-gray-900 dark:text-gray-100">
                {viewOf(open, roomKind)?.label ?? open.title}
              </span>
              <span className="truncate text-xs text-gray-500 dark:text-gray-400">{open.answers}</span>
              {open.id > 0 ? (
                <span className="hidden shrink-0 text-[11px] text-gray-400 dark:text-gray-500 md:inline" data-testid="open-origin">
                  {originOf(open)}
                </span>
              ) : (
                <span className="shrink-0 text-[11px] text-amber-700 dark:text-amber-400" data-testid="open-unsaved">
                  Not saved
                </span>
              )}
              {/* WHAT YOU CAN DO WITH THE VIEW YOU ARE ON, here rather than only
                  in All views. Words, not a bare ×: on a full view a × reads as
                  "close this page", which ‹ Dashboard already does. */}
              <span className="ml-auto flex shrink-0 items-center gap-1.5">
                {renderActions?.(open)}
                {open.id < 0 && onSave && (
                  <button
                    type="button"
                    onClick={() => onSave(open)}
                    title="Keep it in All views, without putting it on the Dashboard"
                    className="rounded border border-gray-300 px-1.5 py-0.5 text-xs text-gray-600 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700"
                    data-testid="open-save"
                  >
                    Save to All views
                  </button>
                )}
                {open.pinned && open.status !== 'dropped' ? (
                  <button
                    type="button"
                    onClick={() => {
                      onPin(open, false);
                      onExpand(null, true);
                    }}
                    title="Take it off the Dashboard. It waits in All views."
                    className="rounded border border-gray-300 px-1.5 py-0.5 text-xs text-gray-600 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700"
                    data-testid="open-unpin"
                  >
                    Unpin
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={() => onPin(open, true)}
                    className="rounded border border-gray-300 px-1.5 py-0.5 text-xs text-gray-600 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700"
                    data-testid="open-pin"
                  >
                    Pin to Dashboard
                  </button>
                )}
                {onDelete && deletable(open) && (
                  <button
                    type="button"
                    onClick={() => onDelete(open)}
                    title="Delete this view for good"
                    className="rounded px-1.5 py-0.5 text-xs text-red-600 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-900/20"
                    data-testid="open-delete"
                  >
                    Delete
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => onExpand(null)}
                  className="rounded border border-gray-300 px-1.5 py-0.5 text-xs text-gray-600 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700"
                >
                  ‹ Dashboard
                </button>
              </span>
            </header>
            {/* THE VIEW'S TABLES AS TABS: Search is its terms, its demand, who
                shows up and who goes after whom. */}
            {(() => {
              const view = viewOf(open, roomKind);
              if (!view || view.tabs.length < 2) return null;
              const has = (k: string) =>
                live.some(d => d.kind === k && !d.subject) || catalogue.some(c => c.kind === k);
              return (
                <div role="tablist" className="flex gap-1 border-b border-gray-100 px-3 dark:border-gray-700/60" data-testid="view-tabs">
                  {view.tabs.filter(t => has(t.kind) || t.kind === open.kind).map(t => (
                    <button
                      key={t.kind}
                      type="button"
                      role="tab"
                      aria-selected={t.kind === open.kind}
                      onClick={() => {
                        if (t.kind === open.kind) return;
                        const d = live.find(x => x.kind === t.kind && !x.subject);
                        if (d) onExpand(d, true);
                        else onOpenKind?.(t.kind);
                      }}
                      className={`-mb-px border-b-2 px-2 py-1.5 text-xs ${
                        t.kind === open.kind
                          ? 'border-indigo-500 font-medium text-indigo-700 dark:text-indigo-200'
                          : 'border-transparent text-gray-500 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-100'
                      }`}
                      data-testid={`view-tab-${t.kind}`}
                    >
                      {t.label}
                    </button>
                  ))}
                </div>
              );
            })()}
            <div className="min-h-0 flex-1 overflow-auto p-3">
              {renderFull ? renderFull(open) : renderCard(open)}
            </div>
          </section>
        ) : listed.length === 0 ? (
          <div className="flex flex-1 items-center justify-center rounded-lg border border-dashed border-gray-300 text-sm text-gray-500 dark:border-gray-600 dark:text-gray-400">
            <div className="text-center">
              <p>Nothing pinned to the Dashboard yet.</p>
              <button
                type="button"
                onClick={() => onPanel('add')}
                className="mt-1 text-indigo-600 hover:underline dark:text-indigo-300"
              >
                Add a view
              </button>
            </div>
          </div>
        ) : (
          <div className="min-h-0 flex-1 overflow-y-auto pr-1" data-testid="board-dashboard">
            {top}
            {/* ONE CARD PER VIEW (see `views.ts`): its face is the view's first
                table with something in it, and opening it opens that table
                with the others as tabs. */}
            <div className="grid grid-cols-[repeat(auto-fill,minmax(16rem,1fr))] gap-3">
              {tiles
                .filter(t => !tileEmpty(t))
                .map(t => {
                  const d = t.members.find(m => !isEmptyCard(m)) ?? t.lead;
                  const badge = t.members.reduce((n, m) => n + (badges?.[m.kind] ?? 0), 0);
                  return (
                    <GlanceCard
                      key={d.id}
                      deliverable={d}
                      title={t.view?.label}
                      badge={badge || undefined}
                      matches={matchesOf(d)}
                      dragging={dragId === d.id}
                      dropSide={dropSideOf(d)}
                      glow={t.members.some(m => glowing.has(m.id))}
                      dragProps={dragProps(d)}
                      onOpen={() => onExpand(d)}
                      onRemove={() => t.members.forEach(m => onPin(m, false))}
                      origin={originOf(d)}
                    >
                      {renderCard(d, 4)}
                    </GlanceCard>
                  );
                })}
            </div>
            {/* EMPTY CARDS FOLD INTO ONE LINE. Two full cards saying "Nothing
                here yet" took the space of two that had findings. */}
            {tiles.some(tileEmpty) && (
              <div className="mt-3 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-gray-500 dark:text-gray-400" data-testid="not-started">
                <span>{'Not started: '}</span>
                {tiles.filter(tileEmpty).map((t, i) => (
                  <span key={t.lead.id}>
                    {i > 0 && <span className="mr-2">·</span>}
                    <button type="button" onClick={() => onExpand(t.lead)} className="text-indigo-600 hover:underline dark:text-indigo-300">
                      {t.view?.label ?? t.lead.title}
                    </button>
                  </span>
                ))}
              </div>
            )}
          </div>
        )}
        {panel && (
          <SlideUpPanel
            title={PANEL_TITLES[panel]}
            onClose={() => onPanel(null)}
            testId={`board-panel-${panel}`}
          >
            {panel === 'add' ? (
              panels.add
            ) : panels[panel].body ? (
              // The page passes null when there is nothing to show (it knows
              // about set-aside noticings, which the count leaves out).
              panels[panel].body
            ) : (
              <p className="text-sm text-gray-500 dark:text-gray-400">Nothing here yet.</p>
            )}
          </SlideUpPanel>
        )}
      </div>
      {open && listOpen && (
        <Drawer side="left" width="w-72 max-w-full" eyebrow="Dashboard" title="Views" onClose={() => setListOpen(false)} fill>
          {list('h-full p-2')}
        </Drawer>
      )}
    </div>
  );
}
